import { faker } from '@faker-js/faker';

import type {
	BarDatum,
	DonutDatum,
	GroupedBarDatum,
	LinePoint,
	StatTile,
} from '@/api/types';

/** One provider's send volume over time — a named line series. */
export interface ProviderVolumeSeries {
	/** Provider name, e.g. 'SES' | 'SendGrid' | 'Postmark' | 'Mailgun'. */
	provider: string;
	/** Daily send counts; reuses the shared LinePoint (date, value) shape. */
	points: LinePoint[];
}

/**
 * One "volume and health" sample. Carries enough dimension tags
 * (full timestamp + application) to filter purely client-side by time-of-day,
 * day-of-week, date-range preset, and application — no server round-trip.
 */
export interface VolumeHealthPoint {
	/** ISO-8601 datetime (full precision — needed for the time-of-day filter). */
	timestamp: string;
	/** Owning application, e.g. 'Policy Portal' | 'Claims' | 'Billing'. */
	application: string;
	sent: number;
	delivered: number;
	failed: number;
}

/** Overview dashboard payload: stat tiles + chart-type series. */
export interface OverviewData {
	statTiles: StatTile[];
	/** Email volume by provider — bar chart (single snapshot). */
	volumeByProvider: BarDatum[];
	/** Provider send volume over time — one line series per provider. */
	volumeByProviderOverTime: ProviderVolumeSeries[];
	/** Delivery-status split — donut chart. */
	statusSplit: DonutDatum[];
	/** Sends per day over the last 14 days — line chart. */
	volumeOverTime: LinePoint[];
	/** Weekly delivered/failed/pending breakdown — grouped-bar chart. */
	weeklyBreakdown: GroupedBarDatum[];
	/**
	 * Volume-and-health aggregate samples over the last ~90 days, tagged for
	 * client-side filtering (time-of-day, day-of-week, date-range, application).
	 */
	volumeAndHealth: VolumeHealthPoint[];
}

const PROVIDERS = ['SES', 'SendGrid', 'Postmark', 'Mailgun'] as const;
const APPLICATIONS = ['Policy Portal', 'Claims', 'Billing'] as const;

/** How many days of history the time-series datasets span. */
const PROVIDER_SERIES_DAYS = 14;
const VOLUME_HEALTH_DAYS = 90;
/** Samples per day for the volume-and-health dataset (every 4 hours). */
const HOURS_OF_DAY = [0, 4, 8, 12, 16, 20] as const;

function pct(value: number): number {
	return Number(value.toFixed(1));
}

export function getOverviewMock(): OverviewData {
	const totalSent = faker.number.int({ min: 8_000, max: 20_000 });
	const delivered = Math.round(totalSent * faker.number.float({ min: 0.9, max: 0.98 }));
	const failed = Math.round((totalSent - delivered) * faker.number.float({ min: 0.4, max: 0.7 }));
	const bounced = Math.round((totalSent - delivered - failed) * faker.number.float({ min: 0.5, max: 0.9 }));
	const pending = Math.max(totalSent - delivered - failed - bounced, 0);

	const statTiles: StatTile[] = [
		{
			id: 'total-sent',
			label: 'Total Sent',
			value: totalSent.toLocaleString('en-US'),
			deltaPct: pct(faker.number.float({ min: -8, max: 18 })),
			trend: 'up',
		},
		{
			id: 'delivery-rate',
			label: 'Delivery Rate',
			value: `${pct((delivered / totalSent) * 100)}%`,
			deltaPct: pct(faker.number.float({ min: -2, max: 3 })),
			trend: 'up',
		},
		{
			id: 'failed',
			label: 'Failed',
			value: failed.toLocaleString('en-US'),
			deltaPct: pct(faker.number.float({ min: -12, max: 6 })),
			trend: 'down',
		},
		{
			id: 'avg-latency',
			label: 'Avg Latency',
			value: `${faker.number.int({ min: 120, max: 900 })}ms`,
			deltaPct: pct(faker.number.float({ min: -15, max: 10 })),
			trend: 'flat',
		},
	];

	const volumeByProvider: BarDatum[] = PROVIDERS.map((label) => ({
		label,
		value: faker.number.int({ min: 500, max: 8_000 }),
	}));

	const volumeByProviderOverTime: ProviderVolumeSeries[] = PROVIDERS.map((provider) => ({
		provider,
		points: Array.from({ length: PROVIDER_SERIES_DAYS }, (_, i) => {
			const date = new Date();
			date.setDate(date.getDate() - (PROVIDER_SERIES_DAYS - 1 - i));
			return {
				date: date.toISOString().slice(0, 10),
				value: faker.number.int({ min: 200, max: 4_000 }),
			};
		}),
	}));

	const statusSplit: DonutDatum[] = [
		{ name: 'Delivered', value: delivered },
		{ name: 'Failed', value: failed },
		{ name: 'Bounced', value: bounced },
		{ name: 'Pending', value: pending },
	];

	const volumeOverTime: LinePoint[] = Array.from({ length: 14 }, (_, i) => {
		const date = new Date();
		date.setDate(date.getDate() - (13 - i));
		return {
			date: date.toISOString().slice(0, 10),
			value: faker.number.int({ min: 400, max: 1_600 }),
		};
	});

	const weeklyBreakdown: GroupedBarDatum[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(
		(label) => ({
			label,
			delivered: faker.number.int({ min: 800, max: 2_400 }),
			failed: faker.number.int({ min: 10, max: 180 }),
			pending: faker.number.int({ min: 5, max: 120 }),
		}),
	);

	const volumeAndHealth: VolumeHealthPoint[] = [];
	for (let day = VOLUME_HEALTH_DAYS - 1; day >= 0; day -= 1) {
		for (const hour of HOURS_OF_DAY) {
			for (const application of APPLICATIONS) {
				const stamp = new Date();
				stamp.setDate(stamp.getDate() - day);
				stamp.setHours(hour, 0, 0, 0);

				const sent = faker.number.int({ min: 40, max: 900 });
				const failed = faker.number.int({ min: 0, max: Math.round(sent * 0.08) });
				volumeAndHealth.push({
					timestamp: stamp.toISOString(),
					application,
					sent,
					delivered: sent - failed,
					failed,
				});
			}
		}
	}

	return {
		statTiles,
		volumeByProvider,
		volumeByProviderOverTime,
		statusSplit,
		volumeOverTime,
		weeklyBreakdown,
		volumeAndHealth,
	};
}
